import { useEffect, useRef, useState } from 'react';
import '../styles/Calendar.css';
import axios from 'axios';
import { BeatLoader, ClipLoader } from 'react-spinners';
import dayjs from 'dayjs';
import DateCalendarServerRequest from '../components/Calendar';
import { months } from '../constants/dayJsMonthTranslation';
import logo from '../assets/images/medicina_icono.jpg';
import HourCard from '../components/HourCard';
import NoProfessionalHourCard from '../components/NoProfessionalHourCard';
import {
  CalendarContainerProps, Client, DataByDate, DataByProfessional, DataFilter, DataHour,
} from './Interfaces';

const baseURL = process.env.REACT_APP_BASEURL;
const nestApiURL = process.env.REACT_APP_NEST_API_URL;
const apiKey = process.env.REACT_APP_API_KEY;

const CalendarContainer = ({
  clientData,
  groupId,
  dataHour,
  dataLabels,
  setIsCharge,
  containerRef,
}: CalendarContainerProps) => {
  const HourTypes = {
    INIT: '0',
    EXAMS: '2',
    MEDICAL_HOURS: '1',
  };

  const [dataByDateData, setDataByDateData] = useState<{ [key: string]: DataByDate }>({});
  const [currentDay, setCurrentDay] = useState<string>(dayjs().format('YYYY-MM-DD'));
  const [existingData, setExistingData] = useState<boolean | null>(false);
  const [hourType] = useState<string>(groupId === '5' ? HourTypes.MEDICAL_HOURS : HourTypes.EXAMS);
  const [noData, setNoData] = useState<boolean>(false);
  const [, setIsOpen] = useState<boolean>(false);
  const noPaginationSpecialtiesIDs = [4, 1, 'Ecografïas Ginecológicas', 'Exámenes Cardiológicos'];

  useEffect(() => {
    getInitialData();
    // eslint-disable-next-line
  }, [dataHour]);

  const resetCalendar = () => {
    setNoData(false);
    setCurrentDay(dayjs().format('YYYY-MM-DD'));
    setExistingData(true);
    setDataByDateData({});
  };

  const getInitialData = () => {
    if (dataHour) {
      /* == LÓGICA ANTIGUA COMENTADA == 
      if (dataHour.doctor !== '') {
        getFutureHoursByDoctor(dataHour, clientData);
      } else {
        getFutureHours(dataHour, clientData);
      }
      */
      
      // Con NestJS, una sola función maneja ambos casos
      getFutureHoursNest(dataHour, clientData);
    }
  };

  // ==========================================
  // LÓGICA ANTIGUA PHP COMENTADA 
  // ==========================================
  /*
  const getFutureHoursByDoctor = (hourData: DataFilter, clientDataF: Client) => {
    let dataByDate: { [key: string]: DataByDate } = {};
    resetCalendar();
    if (hourData.facility === '3') {
      dataByDate = getLaCisterna057NextHours(hourData, clientDataF, dataByDate);
    }
    axios.post(`${baseURL}/alma/external/v1/nextHourProfessional`, { ... }, { headers })
      .then((response: any) => { ... })
      .catch(() => { ... });
  };

  const getFutureHours = (hourData: DataFilter, clientDataF: Client) => {
    let dataByDate: { [key: string]: DataByDate } = {};
    resetCalendar();
    if (hourData.facility === '3') {
      dataByDate = getLaCisterna057NextHours(hourData, clientDataF, dataByDate);
    }
    axios.post(`${baseURL}/alma/external/v1/nextHour`, { ... }, { headers })
      .then((response: any) => { ... })
      .catch(() => { ... });
  };
  
  const getLaCisterna057NextHours = (...) => { ... };
  */


  // ==========================================
  // NUEVA LÓGICA NESTJS
  // ==========================================
  const getFutureHoursNest = (hourData: DataFilter, clientDataF: Client) => {
    resetCalendar();
    
    // Construimos los parámetros para la query string
    const params: any = {
      age: clientDataF.edad,
      categorie: hourData.categorie,
      facility: hourData.facility,
    };

    // Si el usuario seleccionó un doctor en el filtro anterior, lo agregamos
    if (hourData.doctor && hourData.doctor !== '') {
      params.professional_id = hourData.doctor;
    }

    axios.get(`${nestApiURL}/availability-v2`, {
      params: params,
      headers: {
        'API_KEY': apiKey,
      }
    }).then((response: any) => {
      if (response?.data?.status === 'success' && Array.isArray(response?.data?.data) && response.data.data.length > 0) {
        const dataByDate = createDictionaryOfAvailableHoursNest(response.data.data, hourData);
        setDataByDateData(dataByDate);
        groupData(dataByDate);
        setIsCharge(false);
      } else {
        setNoData(true); 
        setExistingData(false); 
        setIsCharge(false);
      }
    }).catch(() => { 
      setNoData(true); 
      setExistingData(false); 
      setIsCharge(false); 
    });
  };

  // Adapta la respuesta JSON de NestJS al formato que usa tu calendario visual
  const createDictionaryOfAvailableHoursNest = (data: any[], hourData: DataFilter) => {
    const dataByDate: { [key: string]: DataByDate } = {};
    
    data.forEach((item: any) => {
      // NestJS envía "date" en formato "YYYY-MM-DD"
      const date = item.date; 
      const facilityId = item.facility_id || hourData.facility;
      
      const newConsult: DataHour = {
        idGroupConsultHour: groupId,
        idProfesional: item.professional_id,
        // Tu frontend esperaba un string con fecha y hora juntas, o la hora. 
        // Armamos el datetime a partir de date y hour de NestJS
        reservationHour: `${item.date} ${item.hour}`,
        profesionalName: item.professional_name,
        // NestJS actualmente no envía rut ni info del profesional en este endpoint, los dejamos vacíos
        rutProfesional: "", 
        infoProfesional: item.professional_info || "", 
        facility: facilityId,
        categorie: hourData.categorie,
        preparacion: item.preparation || hourData.preparacion,
        specialty: hourData.specialty,
        reservationCode: '',
        uid: hourData.uid,
      };

      if (!(date in dataByDate)) {
        dataByDate[date] = {
          fecha: date,
          consultas: [newConsult],
        };
      } else {
        dataByDate[date].consultas.push(newConsult);
      }
    });
    
    return dataByDate;
  };


  const groupData = (thisData: { [key: string]: DataByDate }) => (
    (noPaginationSpecialtiesIDs.includes(parseInt(dataHour.specialty, 10))
      || hourType === HourTypes.MEDICAL_HOURS)
      ? groupDataByProfessional(thisData) : [thisData[currentDay]?.consultas || []]);

  const groupDataByProfessional = (thisData: { [key: string]: DataByDate }) => {
    const newData: { [key: string]: DataByProfessional } = {};
    if (!thisData[currentDay] || !thisData[currentDay].consultas) return newData;
    
    thisData[currentDay].consultas.forEach((consulta: DataHour) => {
      if (newData[consulta.profesionalName]) {
        newData[consulta.profesionalName].consultas.push(consulta);
      } else {
        newData[consulta.profesionalName] = {
          consultas: [consulta],
        };
      }
    });
    return newData;
  };

  const setDaySelected = (values: any) => { setCurrentDay(values); };

  const confirmHour = () => setIsOpen(true);

  return (
    <div className="calendarContainer">
      <div className="calendarElement" ref={containerRef}>
        {Object.keys(dataByDateData).length === 0
          ? (
            <div className="loader">
              {existingData ? (
                <ClipLoader
                  color="#78cd51"
                  loading={Object.keys(dataByDateData).length === 0}
                  size={20}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                />
              ) : (!existingData && noData
                ? <h2 style={{ color: '#3A3A3A' }}>No se han encontrado horarios disponibles con los datos especificados.</h2>
                : <h2>Selecciona los datos de consulta.</h2>)}

            </div>
          ) : (
            <div style={{ width: '100%' }}>
              <div className="hourInfoContainer">
                <div className="upperInfoContainer">
                  <div className="upperLeftContainer">
                    <label className="labelForm">Especialidad</label>
                    <p style={{ color: 'black', fontSize: 15 }}>
                      {dataLabels.specialty}
                    </p>
                  </div>
                  <div className="upperRightContainer">
                    <label className="labelForm">Edad</label>
                    <p style={{ color: 'black', fontSize: 15 }}>
                      {clientData.edad}
                    </p>
                  </div>
                </div>
                <div className="lowerInfoContainer">
                  <div className="upperLeftContainer">
                    <label className="labelForm">Prestación</label>
                    <p style={{ color: 'black', fontSize: 15 }}>
                      {dataLabels.category.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                    </p>
                  </div>
                  <div className="upperRightContainer">
                    <label className="labelForm">Sucursal</label>
                    <p style={{ color: 'black', fontSize: 15 }}>
                      {dataLabels.facility.name}
                    </p>
                  </div>
                </div>
              </div>

              <DateCalendarServerRequest
                setDayFunction={setDaySelected}
                data={dataByDateData}
              />
              <div style={{ width: '100%' }}>
                {currentDay && dataByDateData[currentDay] && (
                  (noPaginationSpecialtiesIDs.includes(parseInt(dataHour.specialty, 10))
                    || hourType === HourTypes.MEDICAL_HOURS)
                    ? (
                      Object.keys(groupData(dataByDateData)).map(
                        (professionalName: any) => (
                          <>
                            <HourCard
                              key={professionalName}
                              docName={professionalName}
                              dataHour={
                                groupDataByProfessional(
                                  dataByDateData,
                                )[professionalName].consultas
                              }
                              dataClient={clientData}
                              confirmHour={confirmHour}
                              dataLabels={dataLabels}
                            />
                          </>


                        ),
                      )
                    ) : (
                      <NoProfessionalHourCard
                        docName=""
                        dataHour={dataByDateData[currentDay].consultas}
                        dataClient={clientData}
                        confirmHour={confirmHour}
                        dataLabels={dataLabels}
                      />
                    ))}
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

export default CalendarContainer;
